import {useCallback, useEffect, useRef, useState} from 'react';

import config from '../config';
import {getBffHeaders} from '../utils/apiHeaders';
import {getPlaybookAgentRefs, getRootAgentRef, resolveRefNames, scopedAgentName} from '../utils/playbookGraph';
import AdkClient from '../AdkClient';
import miaFlowAuth from '../auth/MiaFlowAuth';

const AI_API_PREFIX = 'api/catalog';

/**
 * Playbook resolution + "custom AI instructions" state, extracted out of
 * MainChat (App.jsx) as part of the DDD refactor (see tender-roaming-bentley.md,
 * Phase 3). Owns `customAIInstructions*`/`playbookLocksTools` and the two
 * mount-time effects that can apply a playbook (one from `?playbookId=` in
 * the URL, one from a per-mode default saved in localStorage), plus the
 * single canonical `handleApplyAIInstructions` — previously that function
 * AND the URL-playbook effect's own local `applyPlaybook` each independently
 * re-implemented the same tool-collection/normalization logic; here the URL
 * effect resolves *which* playbook to apply (its own concern — sessionStorage
 * hand-off from PlaybookLauncher, else an API lookup by id) and then calls
 * this hook's own `handleApplyAIInstructions` to actually apply it, so
 * there's exactly one implementation of "how a playbook gets applied."
 *
 * `buildPlaybookInstructions` (the ~8-line instructions-string template) is
 * likewise shared by both the URL effect and the default-playbook effect
 * (previously copy-pasted in both).
 *
 * `useMcpTools` needs THIS hook's `customAIInstructionsPlaybookData`/
 * `playbookToolIdsRef` as its own inputs (see useMcpTools.js's doc comment),
 * while this hook needs useMcpTools's `setSelectedTools`/`availableToolsRef`/
 * `miaFlowToolIdsRef` — a genuine two-way dependency between the two hooks.
 * Broken via `toolsApiRef`: a single ref created once by the caller (App.jsx)
 * before either hook is called, passed to THIS hook (which only reads
 * `toolsApiRef.current.*` lazily inside callbacks/effects, never during the
 * initial render), then populated with useMcpTools's actual return value
 * right after both hooks have been called. Safe because React finishes an
 * entire render's hook calls (and this synchronous assignment) before any
 * effect or event callback from either hook can run.
 */
export function usePlaybookApplication({
    location,
    authorizedFetch,
    hideCanvas,
    setAiMode,
    showToast,
    toolsApiRef,
}) {
    const [customAIInstructions, setCustomAIInstructions] = useState('');
    const [customAIInstructionsName, setCustomAIInstructionsName] = useState('');
    const [customAIInstructionsPlaybookData, setCustomAIInstructionsPlaybookData] = useState(null); // Playbook data when instructions come from a playbook
    const [playbookLocksTools, setPlaybookLocksTools] = useState(false); // True only when the active playbook defines tools
    const prePlaybookSelectedToolsRef = useRef(undefined); // Saved selectedTools before a playbook was applied (for reset)
    const playbookToolIdsRef = useRef([]); // Tool IDs set by the active playbook (locked from user deselection)

    // Shared instructions-string template — previously duplicated inline in
    // both the URL-playbook effect and the default-playbook effect.
    const buildPlaybookInstructions = useCallback((playbook) => {
        const agentNames = getPlaybookAgentRefs(playbook).map(a => a.name || a.itemId).filter(Boolean);
        const promptNames = resolveRefNames(playbook.prompts);
        const specNames = resolveRefNames(playbook.specs);
        const skillNames = resolveRefNames(playbook.skills);

        //TODO here mus the passed to ADK and ADK decide what to use and not all in plaintext!
        let instructions = `[PLAYBOOK: ${playbook.name}]\n`;
        if (playbook.description) instructions += `${playbook.description}\n\n`;
        if (agentNames.length) instructions += `Active agents: ${agentNames.join(', ')}\n`;
        if (promptNames.length) instructions += `Loaded prompts: ${promptNames.join(', ')}\n`;
        if (specNames.length) instructions += `Loaded specs: ${specNames.join(', ')}\n`;
        if (skillNames.length) instructions += `Active skills: ${skillNames.join(', ')}\n`;
        return instructions;
    }, []);

    const handleApplyAIInstructions = useCallback(async (instructions, promptName = '', agentData = null, playbookData = null) => {
        // useMcpTools's API — populated by App.jsx right after both hooks are
        // called (see this file's top doc comment for why it's indirected
        // through a ref instead of a direct parameter).
        const {setSelectedTools, availableToolsRef, miaFlowToolIdsRef} = toolsApiRef.current;

        // When clearing a playbook (playbookData is null), restore the previously saved tool selection.
        // null means no pre-existing selection → reset triggers the Mia Flow Tools default.
        if (!playbookData && prePlaybookSelectedToolsRef.current !== undefined) {
            const restored = prePlaybookSelectedToolsRef.current;
            prePlaybookSelectedToolsRef.current = undefined;
            playbookToolIdsRef.current = [];
            setPlaybookLocksTools(false);
            setSelectedTools(restored !== null ? restored : miaFlowToolIdsRef.current);
        }

        setCustomAIInstructions(instructions);
        setCustomAIInstructionsName(promptName);
        setCustomAIInstructionsPlaybookData(playbookData);
        window.dispatchEvent(new CustomEvent('activePlaybookChanged', { detail: playbookData }));

        // When applying a playbook, derive selected tools from all agents + playbook-level tools
        if (playbookData) {
            setAiMode('agent');

            const tenantId = miaFlowAuth.getSelectedTenant()?.tenantId;
            const baseUrl = config.bffUrl || window.location.origin;
            const agentRefs = getPlaybookAgentRefs(playbookData);
            let allAgentTools = [];
            if (agentRefs.length > 0 && tenantId) {
                const toolsPerAgent = await Promise.all(
                    agentRefs.map(async (agentRef) => {
                        const agentItemId = agentRef.itemId || agentRef.id;
                        if (!agentItemId) return [];
                        try {
                            const resp = await authorizedFetch(
                                `${baseUrl}/${AI_API_PREFIX}/ai-agents/${tenantId}/${agentItemId}/latest`,
                                { headers: getBffHeaders() }
                            );
                            if (resp.ok) {
                                const result = await resp.json();
                                const fetchedAgent = result.agent || result.data || result;
                                return fetchedAgent.resources?.tools || [];
                            }
                        } catch (e) {
                            console.error('Failed to fetch agent tools for playbook:', e);
                        }
                        return [];
                    })
                );
                allAgentTools = toolsPerAgent.flat();
            }
            const playbookDirectTools = (playbookData.tools || [])
                .map(t => t.itemId || t.name || t)
                .filter(Boolean);
            const rawTools = [...new Set([...allAgentTools, ...playbookDirectTools])];

            // Normalize tool IDs: agents store tools by metadata.name slug (e.g. "list-flow-types").
            // Map each raw ID to a known tool.id, also handling legacy category:name and snake_case.
            const resolveToolIds = (rawIds) => {
                const tools = availableToolsRef.current;
                if (tools.length === 0) return rawIds;
                const knownIds = new Set(tools.map(t => t.id));
                const nameToId = new Map(tools.map(t => [t.name, t.id]));
                const toKebab = s => s.toLowerCase().replace(/_/g, '-').replace(/\s+/g, '-');
                return [...new Set(rawIds.flatMap(rawId => {
                    if (knownIds.has(rawId)) return [rawId];
                    if (nameToId.has(rawId)) return [nameToId.get(rawId)];
                    // Strip legacy "category:name" format
                    const colonIdx = rawId.indexOf(':');
                    const stripped = colonIdx >= 0 ? rawId.substring(colonIdx + 1) : rawId;
                    if (knownIds.has(stripped)) return [stripped];
                    if (nameToId.has(stripped)) return [nameToId.get(stripped)];
                    // Try kebab-case variant (snake_case from old ADK / playbooks)
                    const kebab = toKebab(stripped);
                    if (knownIds.has(kebab)) return [kebab];
                    const kebabRaw = toKebab(rawId);
                    if (knownIds.has(kebabRaw)) return [kebabRaw];
                    return [];
                }))];
            };
            const allTools = resolveToolIds(rawTools);
            playbookToolIdsRef.current = allTools;

            if (allTools.length > 0) {
                setPlaybookLocksTools(true);
                setSelectedTools(prev => {
                    if (prePlaybookSelectedToolsRef.current === undefined) {
                        prePlaybookSelectedToolsRef.current = prev;
                    }
                    return allTools;
                });
            } else {
                setPlaybookLocksTools(true);
                setSelectedTools(prev => {
                    if (prePlaybookSelectedToolsRef.current === undefined) {
                        prePlaybookSelectedToolsRef.current = prev;
                    }
                    return [];
                });
            }
        }

        // Update AdkClient app name override.
        // Playbook with agents → scoped catalog name of root agent (takes priority).
        // Custom agent without playbook → use promptName.
        // Otherwise → reset to default hub.
        if (playbookData) {
            const rootRef = getRootAgentRef(playbookData);
            if (rootRef) {
                const refId = rootRef.itemId || rootRef.id || rootRef.name;
                AdkClient.setOverrideAppName(scopedAgentName(playbookData.name, refId));
            } else {
                AdkClient.setOverrideAppName(null);
            }
        } else if (agentData) {
            AdkClient.setOverrideAppName(promptName);
        } else {
            AdkClient.setOverrideAppName(null);
        }

        if (instructions) {
            showToast(promptName ? `AI Playbook: ${promptName}` : 'AI Playbook applied to this session', 'success');
        } else {
            showToast('AI Playbook removed', 'info');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Apply a playbook found via `?playbookId=` in the URL — resolution only
    // (sessionStorage hand-off from PlaybookLauncher, else an API lookup),
    // application itself goes through the shared handleApplyAIInstructions.
    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const playbookId = searchParams.get('playbookId');
        if (!playbookId) return;

        // Try sessionStorage first (set by PlaybookLauncher just before navigating)
        let playbookData = null;
        try {
            const stored = sessionStorage.getItem('mia_active_playbook');
            if (stored) {
                playbookData = JSON.parse(stored);
                if (playbookData?.itemId !== playbookId) playbookData = null; // stale
            }
        } catch (_) { /* ignore */ }

        const applyPlaybook = (playbook) => {
            sessionStorage.removeItem('mia_active_playbook');
            handleApplyAIInstructions(buildPlaybookInstructions(playbook), playbook.name, null, playbook);
        };

        if (playbookData) {
            applyPlaybook(playbookData);
        } else {
            // Fetch from API — scope comes from x-mia-acl-context (getBffHeaders), not tenantId.
            const baseUrl = config.bffUrl || window.location.origin;
            const qs = new URLSearchParams({ page: '1', perPage: '100' });
            fetch(`${baseUrl}/${AI_API_PREFIX}/ai-playbooks?${qs}`, {
                headers: getBffHeaders()
            })
                .then(r => r.json())
                .then(result => {
                    if (result.success && result.data) {
                        const found = (result.data.items || []).find(i => i.itemId === playbookId);
                        if (found) applyPlaybook(found);
                    }
                })
                .catch(() => {});
        }
    // Only run once when playbookId first appears in the URL
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search]);

    // Auto-apply the default playbook for this mode (chat vs code) if:
    //   1. No ?playbookId in the current URL (URL-based playbook takes priority)
    //   2. No playbook is already active (customAIInstructionsPlaybookData is null)
    //   3. A default playbook ID is saved in localStorage for this mode
    // Runs once on mount so it doesn't interfere with later navigation.
    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        if (searchParams.get('playbookId')) return; // URL-based playbook wins

        const localStorageKey = hideCanvas ? 'DEFAULT_CHAT_PLAYBOOK_ID' : 'DEFAULT_CODE_PLAYBOOK_ID';
        const defaultPlaybookId = localStorage.getItem(localStorageKey);
        if (!defaultPlaybookId) return;

        const baseUrl = config.bffUrl || window.location.origin;
        const qs = new URLSearchParams({ page: '1', perPage: '100' });

        authorizedFetch(`${baseUrl}/${AI_API_PREFIX}/ai-playbooks?${qs}`, { headers: getBffHeaders() })
            .then(r => r.json())
            .then(result => {
                if (result.success && result.data) {
                    const found = (result.data.items || []).find(i => i.itemId === defaultPlaybookId);
                    if (found) {
                        handleApplyAIInstructions(buildPlaybookInstructions(found), found.name, null, found);
                    }
                }
            })
            .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Only run once on mount

    return {
        customAIInstructions,
        setCustomAIInstructions,
        customAIInstructionsName,
        setCustomAIInstructionsName,
        customAIInstructionsPlaybookData,
        setCustomAIInstructionsPlaybookData,
        playbookLocksTools,
        setPlaybookLocksTools,
        prePlaybookSelectedToolsRef,
        playbookToolIdsRef,
        buildPlaybookInstructions,
        handleApplyAIInstructions,
    };
}
